import {
  DevHubBox,
  DevHubDynamicIcon,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  AdditionalDetails,
  AppDetails,
  CorporateEntity,
  EntityProp,
  PrivacyQuestion,
  Survey,
} from './types/onboardingTypes';
import { AuthenticationData } from './AuthenticationDetails';
import AppInfoItem from '../common/AppInfoItem';
import { privacySurvey } from './privacySurvey';
import AppEntityIcon from '../common/AppEntityIcon';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import { ADGroupsDisplay } from './ADGroupsDisplay';
import { useNotification } from '../notifications/hooks';

interface Props {
  formData: AppDetails;
  additionalData: AdditionalDetails;
  privacyData: any;
  authenticationData: AuthenticationData;
  EntityDetail: EntityProp;
  handleSubmitButton: (isDisabled: boolean, isLoading: boolean) => void;
  toggleSubmit: boolean;
}

/**
 * Component to handle new Application creation in fusion
 * @param props component props
 * @param props.formData Object to hold application details
 */
function AppCreationSubmit(props: Props) {
  const {
    formData,
    additionalData,
    privacyData,
    authenticationData,
    toggleSubmit,
    EntityDetail,
    handleSubmitButton,
  } = props;
  const [newAppId, setNewAppID] = useState<number>(-99);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<string>('');
  const [notificationSent, setNotificationSent] = useState<boolean>(false);
  const isPrivacySurveyEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.PRIVACY_SURVEY,
  );
  const { sendNotification, getCurrentUser } = useNotification();

  const [createApplication, { loading, error }] = useMutation(
    EntityDetail.fusionQuery,
    {
      onCompleted: data => {
        const appId =
          data?.resources?.application?.createApplication?.applicationId;
        setNewAppID(appId);
      },
    },
  );

  // Get current user on component mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const user = await getCurrentUser();
      if (user) {
        setCurrentUser(user);
      }
    };
    fetchCurrentUser();
  }, [getCurrentUser]);

  // Send notification when app is created
  useEffect(() => {
    const sendOwnerNotification = async () => {
      if (
        newAppId > 0 &&
        formData?.owner?.login &&
        !notificationSent &&
        currentUser
      ) {
        setNotificationSent(true);

        const ownerEntityRef = `user:default/${formData.owner.login.toLowerCase()}`;
        const currentUserEntityRef = currentUser || 'user:default/unknown';

        // Extract username from entity ref (e.g., "user:default/username" -> "username")
        const currentUsername =
          currentUserEntityRef.split('/').pop() || 'A user';

        try {
          await sendNotification({
            recipient: ownerEntityRef,
            title: `New Application Created: ${formData.name}`,
            description: `${currentUsername} created application "${formData.name}" (ID: ${newAppId}) and assigned you as the owner.`,
            link: `/catalog/default/system/${newAppId}`,
            severity: 'normal',
            topic: 'application-onboarding',
          });
        } catch (notificationError) {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to send notification to app owner:',
            notificationError,
          );
          // Don't fail the app creation if notification fails
        }
      }
    };

    sendOwnerNotification();
  }, [
    newAppId,
    formData?.owner?.login,
    formData.name,
    notificationSent,
    currentUser,
    sendNotification,
  ]);

  const getSurveyResponse = (): PrivacyQuestion[] => {
    const survey: PrivacyQuestion[] = [];

    Object.entries(privacyData as Survey).map(([key, value]) => {
      survey.push({ question: key, answer: value });
    });

    return survey;
  };

  const getBaseApplicationCreateData = () => {
    const data = {
      appGroup: EntityDetail?.appGroupId,
      description: formData?.description.trim(),
      name: formData?.name.trim(),
      ownerLogin: String(formData?.owner?.login),
      serviceLevelTier: formData?.serviceLevelTier,
      status: formData?.status,
      url: formData?.url,
    };
    return { applicationBaseInput: data };
  };

  const getCableData = () => {
    const baseApplicationCreateData = {
      ...getBaseApplicationCreateData().applicationBaseInput,
      appGroup: additionalData?.appGroup?.applicationId
        ? Number(additionalData?.appGroup?.applicationId)
        : 18902,
    };
    const data = {
      applicationBaseInput: baseApplicationCreateData,
      iopGroup: additionalData?.iopGroup,
      iopOwner: additionalData?.iopOwner,
      securityPoc: additionalData?.securityPoc?.login,
      informationClassificationPolicy: additionalData?.infoClass?.name,
      registerSso: authenticationData?.requiresLogin,
      iopBusinessCapabilities: ConfigHelper.instance.isFeatureEnabled(
        Feature.BUSINESS_CAPABILITIES,
        false,
      )
        ? additionalData?.businessCapabilities?.map(bc => bc.sysId)
        : undefined,
    };
    if (isPrivacySurveyEnabled) {
      return {
        ...data,
        privacySurvey: { responses: getSurveyResponse() },
        consumerType: formData?.consumerType,
        systemType: formData?.systemType,
      };
    }
    return data;
  };

  const handleSubmit = () => {
    setSubmitted(true);
    const submitData =
      EntityDetail.name === CorporateEntity.CABLE
        ? getCableData()
        : getBaseApplicationCreateData();
    createApplication({ variables: { input: submitData } });
  };

  useEffect(() => {
    if (toggleSubmit) {
      handleSubmit();
    }
  }, [toggleSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isDisabled = (submitted || loading) && error === undefined;
    const isLoading: boolean = submitted && loading;
    handleSubmitButton(isDisabled, isLoading);
  }, [submitted, loading, error]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {submitted && !loading && !error ? (
        <DevHubBox
          role="status"
          aria-live="polite"
          p={3}
          borderRadius={2}
          boxShadow={1}
          bgcolor="background.paper"
        >
          <DevHubBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={3}
          >
            <DevHubBox display="flex" alignItems="center">
              <DevHubDynamicIcon
                fontSize="large"
                iconName="CheckCircleOutline"
                color="success"
                aria-hidden="true"
              />
              <DevHubBox ml={2}>
                <DevHubTypography variant="h6">
                  Application {formData?.name} created successfully
                </DevHubTypography>
              </DevHubBox>
            </DevHubBox>
            <DevHubBox mb={2} ml={3} textAlign="center">
              <DevHubTypography variant="body1" color="textSecondary">
                Application ID <code>{newAppId?.toString()}</code> was created.
              </DevHubTypography>
            </DevHubBox>
          </DevHubBox>

          <DevHubBox
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            mb={3}
          >
            <AppInfoItem
              primaryText="New Application ID:"
              secondaryText={newAppId?.toString()}
            />

            <ADGroupsDisplay applicationId={newAppId} />
          </DevHubBox>
          <DevHubBox
            role="separator"
            aria-hidden="true"
            height={1}
            bgcolor="divider"
            my={2}
          />
        </DevHubBox>
      ) : (
        <DevHubBox margin="small">
          <DevHubTypography variant="h3">Application Info</DevHubTypography>
          <AppInfoItem
            primaryText="Corporate Entity:"
            secondaryText={<AppEntityIcon entity={formData?.entity} />}
          />
          <AppInfoItem
            primaryText="Application Name:"
            secondaryText={formData?.name}
          />
          <AppInfoItem primaryText="Status:" secondaryText={formData?.status} />
          <AppInfoItem
            primaryText="Service Level:"
            secondaryText={String(formData?.serviceLevelTier)}
          />
          <AppInfoItem
            primaryText="Application Owner:"
            secondaryText={formData?.owner?.login as string}
          />
          <ConfigFeatureCheck feature={Feature.PRIVACY_SURVEY}>
            {formData.entity === CorporateEntity.CABLE && (
              <>
                <AppInfoItem
                  primaryText="Consumer Type:"
                  secondaryText={formData?.consumerType}
                />
                <AppInfoItem
                  primaryText="System Type:"
                  secondaryText={formData?.systemType}
                />
              </>
            )}
          </ConfigFeatureCheck>
          <AppInfoItem
            primaryText="Description:"
            secondaryText={formData?.description}
          />
          {Object.keys(additionalData).length > 0 && (
            <DevHubTypography variant="h3">Additional Info</DevHubTypography>
          )}
          {formData?.url && (
            <AppInfoItem
              primaryText="URL:"
              secondaryText={String(formData?.url)}
            />
          )}
          {additionalData?.iopOwner && (
            <AppInfoItem
              primaryText="IOP Owner:"
              secondaryText={String(additionalData?.iopOwner)}
            />
          )}
          {additionalData?.iopGroup && (
            <AppInfoItem
              primaryText="IOP Group:"
              secondaryText={String(additionalData?.iopGroup)}
            />
          )}
          {additionalData?.securityPoc?.login && (
            <AppInfoItem
              primaryText="Security POC:"
              secondaryText={additionalData?.securityPoc?.login as string}
            />
          )}
          {additionalData?.appGroup && (
            <AppInfoItem
              primaryText="Application Group:"
              secondaryText={additionalData?.appGroup?.name}
            />
          )}
          {additionalData?.infoClass && (
            <AppInfoItem
              primaryText="Information Classification:"
              secondaryText={additionalData?.infoClass?.name ?? ''}
            />
          )}
          {ConfigHelper.instance.isFeatureEnabled(
            Feature.BUSINESS_CAPABILITIES,
            false,
          ) &&
            additionalData?.businessCapabilities &&
            additionalData.businessCapabilities.length > 0 && (
              <AppInfoItem
                primaryText="Business Capabilities:"
                secondaryText={additionalData.businessCapabilities
                  .map(bc => bc.name)
                  .join(', ')}
              />
            )}
          {authenticationData?.requiresLogin !== undefined && (
            <DevHubBox mt={3}>
              <DevHubTypography variant="h3">
                Authentication Details
              </DevHubTypography>
              <AppInfoItem
                primaryText="Requires User Login:"
                secondaryText={authenticationData.requiresLogin ? 'Yes' : 'No'}
              />
            </DevHubBox>
          )}
          <ConfigFeatureCheck feature={Feature.PRIVACY_SURVEY}>
            <DevHubBox mt={3}>
              {Object.keys(privacyData).length > 0 && (
                <DevHubTypography variant="h3">
                  Privacy Details
                </DevHubTypography>
              )}
              {Object.entries(privacyData as Survey).map(([key, value]) => {
                return (
                  <AppInfoItem
                    primaryText={privacySurvey?.[key]?.text}
                    secondaryText={value ? 'Yes' : 'No'}
                  />
                );
              })}
            </DevHubBox>
          </ConfigFeatureCheck>
        </DevHubBox>
      )}
      <DevHubBox display="flex" margin="small">
        {submitted && error && (
          <DevHubBox ml={2}>
            <DevHubTypography
              variant="subtitle1"
              color="error"
            >{`Error submitting: ${error?.message}`}</DevHubTypography>
          </DevHubBox>
        )}
      </DevHubBox>
    </>
  );
}

export default AppCreationSubmit;
